/**
 * Admin Billing Zod validation schemas.
 *
 * Authoritative frontend contract for admin billing server actions — mirrors
 * docs/openapi.v.2.3.70.json.
 */

import { z } from "zod";

import { uuidSchema } from "@/lib/validation/schemas/common";

// =============================================================================
// Shared primitives
// =============================================================================

const slugSchema = z
  .string()
  .trim()
  .min(1, "Slug is required")
  .max(100)
  .regex(/^[a-z0-9-]+$/, "Slug must be lowercase alphanumeric with hyphens");

const stripePriceIdSchema = z
  .string()
  .trim()
  .min(1, "Stripe price ID is required")
  .regex(/^price_/, "Must be a Stripe Price ID (price_…)");

const stripeProductIdSchema = z
  .string()
  .trim()
  .min(1, "Stripe product ID is required")
  .regex(/^prod_/, "Must be a Stripe Product ID (prod_…)");

const currencySchema = z
  .string()
  .trim()
  .length(3, "Currency must be a 3-letter ISO code")
  .transform((v) => v.toLowerCase());

const billingIntervalSchema = z.enum(["monthly", "yearly"]);
const billingTierSchema = z.enum(["trial", "free", "paid"]);
const addonChargeTypeSchema = z.enum(["recurring", "one_time"]);
const stripeProductTypeSchema = z.enum(["flat", "metered", "one_time"]);
const prorationBehaviorSchema = z.enum([
  "none",
  "create_prorations",
  "always_invoice",
]);

// =============================================================================
// Stripe primitives
// =============================================================================

export const createStripeProductSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  description: z.string().trim().max(2000).nullish(),
  product_type: stripeProductTypeSchema,
  metadata: z.record(z.string(), z.string()).nullish(),
});

export const createStripePriceSchema = z
  .object({
    stripe_product_id: stripeProductIdSchema,
    unit_amount: z
      .number()
      .int("Amount must be an integer (cents)")
      .positive("Amount must be greater than 0"),
    currency: currencySchema,
    billing_interval: billingIntervalSchema.nullish(),
    price_type: stripeProductTypeSchema,
    meter_event_name: z.string().trim().min(1).max(100).nullish(),
  })
  .superRefine((v, ctx) => {
    if (v.price_type !== "one_time" && !v.billing_interval) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Billing interval is required for flat/metered prices",
        path: ["billing_interval"],
      });
    }
    if (v.price_type === "metered" && !v.meter_event_name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Meter event name is required for metered prices",
        path: ["meter_event_name"],
      });
    }
  });

export const createStripeMeterSchema = z.object({
  display_name: z.string().trim().min(1, "Display name is required").max(200),
  event_name: z
    .string()
    .trim()
    .min(1, "Event name is required")
    .max(100)
    .regex(
      /^[a-z0-9_]+$/,
      "Event name must be lowercase alphanumeric with underscores",
    ),
});

export const createShippingRateSchema = z.object({
  display_name: z.string().trim().min(1, "Display name is required").max(100),
  amount_cents: z.number().int().min(0, "Amount cannot be negative"),
  currency: currencySchema,
  metadata: z.record(z.string(), z.string()).nullish(),
});

// =============================================================================
// Coupons
// =============================================================================

const couponDurationSchema = z.enum(["once", "repeating", "forever"]);

export const createCouponSchema = z
  .object({
    duration: couponDurationSchema,
    duration_in_months: z.number().int().min(1).nullish(),
    percent_off: z
      .number()
      .gt(0, "Percent off must be greater than 0")
      .max(100, "Percent off cannot exceed 100")
      .nullish(),
    amount_off_cents: z
      .number()
      .int("Amount must be an integer (cents)")
      .positive("Amount must be greater than 0")
      .nullish(),
    currency: currencySchema.nullish(),
    name: z.string().trim().max(200).nullish(),
    max_redemptions: z.number().int().min(1).nullish(),
    applies_to_product_ids: z
      .union([z.string(), z.array(z.string()), z.null()])
      .optional()
      .transform((raw): string[] | null => {
        if (raw == null || raw === "") return null;
        const tokens = Array.isArray(raw)
          ? raw.map((value) => value.trim())
          : raw.split(/[\s,]+/).map((value) => value.trim());
        const ids = [...new Set(tokens.filter(Boolean))];
        return ids.length > 0 ? ids : null;
      })
      .superRefine((ids, ctx) => {
        if (ids == null) return;
        if (ids.length > 20) {
          ctx.addIssue({
            code: "custom",
            message: "At most 20 product IDs",
          });
          return;
        }
        if (ids.some((id) => !stripeProductIdSchema.safeParse(id).success)) {
          ctx.addIssue({
            code: "custom",
            message: "Each ID must be a Stripe Product ID (prod_…)",
          });
        }
      }),
  })
  .superRefine((v, ctx) => {
    const hasPercent = v.percent_off != null;
    const hasAmount = v.amount_off_cents != null;
    if (hasPercent === hasAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide exactly one of percent off or fixed amount off",
        path: hasAmount ? ["amount_off_cents"] : ["percent_off"],
      });
    }
    if (hasAmount && !v.currency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Currency is required for fixed amount coupons",
        path: ["currency"],
      });
    }
    if (v.duration === "repeating" && v.duration_in_months == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Duration in months is required when duration is repeating",
        path: ["duration_in_months"],
      });
    }
    if (v.duration !== "repeating" && v.duration_in_months != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Duration in months is only allowed when duration is repeating",
        path: ["duration_in_months"],
      });
    }
  });

// =============================================================================
// Plans
// =============================================================================

export const meteredPriceInputSchema = z
  .object({
    stripe_price_id: stripePriceIdSchema,
    included_units: z
      .number()
      .int()
      .min(0, "Included units cannot be negative"),
    flex_units: z.number().int().min(0),
  })
  .superRefine((v, ctx) => {
    if (v.flex_units > v.included_units) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Flex units cannot exceed included units",
        path: ["flex_units"],
      });
    }
  });

export const createPlanSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(200),
    slug: slugSchema,
    description: z.string().trim().max(1000).nullish(),
    details: z.string().trim().max(10000).nullish(),
    billing_interval: billingIntervalSchema,
    stripe_flat_price_id: stripePriceIdSchema,
    metered_prices: z
      .array(meteredPriceInputSchema)
      .min(1, "At least one metered price is required"),
    is_enterprise: z.boolean(),
    client_id: z
      .union([uuidSchema, z.literal("").transform(() => null)])
      .nullish(),
    addon_slugs: z.array(slugSchema).max(20),
  })
  .superRefine((v, ctx) => {
    if (v.is_enterprise && !v.client_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Client is required for enterprise plans",
        path: ["client_id"],
      });
    }
  });

export const updatePlanSchema = z.object({
  name: z.string().trim().min(1).max(200).nullish(),
  description: z.string().trim().max(1000).nullish(),
  details: z.string().trim().max(10000).nullish(),
  stripe_flat_price_id: stripePriceIdSchema.nullish(),
  metered_prices: z
    .array(meteredPriceInputSchema)
    .min(1, "At least one metered price is required")
    .nullish(),
  addon_slugs: z.array(slugSchema).max(20).nullish(),
});

export const applyPricingSchema = z.object({
  target_plan_id: uuidSchema,
  proration_behavior: prorationBehaviorSchema,
});

// =============================================================================
// Add-ons
// =============================================================================

export const addonShippingConfigSchema = z.object({
  display_name: z.string().trim().min(1).max(100).nullish(),
  amount_cents: z.number().int().min(0).nullish(),
  currency: currencySchema.nullish(),
  stripe_shipping_rate_id: z.string().trim().min(1).nullish(),
});

export const createAddonSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(200),
    slug: slugSchema,
    description: z.string().trim().max(2000).nullish(),
    details: z.string().trim().max(10000).nullish(),
    charge_type: addonChargeTypeSchema,
    billing_interval: billingIntervalSchema.nullish(),
    feature_key: z.string().trim().max(100).nullish(),
    requires_shipping: z.boolean(),
    stripe_flat_price_id: stripePriceIdSchema,
    shipping: addonShippingConfigSchema.nullish(),
  })
  .superRefine((v, ctx) => {
    if (v.charge_type === "recurring" && !v.billing_interval) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Billing interval is required for recurring add-ons",
        path: ["billing_interval"],
      });
    }
    if (v.requires_shipping && v.charge_type !== "one_time") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Shipping is only supported for one-time add-ons",
        path: ["requires_shipping"],
      });
    }
  });

export const updateAddonSchema = z.object({
  name: z.string().trim().min(1).max(200).nullish(),
  description: z.string().trim().max(2000).nullish(),
  details: z.string().trim().max(10000).nullish(),
  billing_interval: billingIntervalSchema.nullish(),
  feature_key: z.string().trim().max(100).nullish(),
  stripe_flat_price_id: stripePriceIdSchema.nullish(),
  requires_shipping: z.boolean().nullish(),
  shipping: addonShippingConfigSchema.nullish(),
  is_active: z.boolean().nullish(),
});

// =============================================================================
// Client billing config
// =============================================================================

export const clientBillingConfigSchema = z
  .object({
    billing_tier: billingTierSchema,
    initialization_date: z
      .string()
      .trim()
      .refine((v) => v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v), {
        message: "Date must be YYYY-MM-DD",
      })
      .nullish(),
    contract_start_date: z
      .string()
      .trim()
      .refine((v) => v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v), {
        message: "Date must be YYYY-MM-DD",
      })
      .nullish(),
    contract_end_date: z
      .string()
      .trim()
      .refine((v) => v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v), {
        message: "Date must be YYYY-MM-DD",
      })
      .nullish(),
    notes: z.string().trim().max(5000).nullish(),
  })
  .superRefine((v, ctx) => {
    if (v.billing_tier === "trial" && !v.initialization_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Trial end date is required when billing tier is trial",
        path: ["initialization_date"],
      });
    }
  });

// =============================================================================
// One-off invoice
// =============================================================================

export const oneOffInvoiceLineSchema = z.object({
  slug: slugSchema,
  quantity: z.number().int().positive("Quantity must be at least 1"),
});

export const oneOffInvoiceSchema = z.object({
  client_id: uuidSchema,
  plans: z
    .array(oneOffInvoiceLineSchema)
    .min(1, "At least one plan line is required"),
  addons: z.array(oneOffInvoiceLineSchema),
  period_start: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Period start must be YYYY-MM-DD"),
  period_end: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Period end must be YYYY-MM-DD"),
  description: z.string().trim().max(500).nullish(),
});

// =============================================================================
// Credits & discounts
// =============================================================================

export const adminMeterGrantSchema = z.object({
  client_id: uuidSchema,
  meter_id: uuidSchema,
  units: z
    .number()
    .int("Units must be an integer")
    .positive("Units must be greater than 0")
    .max(1_000_000, "Units cannot exceed 1,000,000"),
  period: z.enum(["current", "next"]),
  note: z
    .string()
    .trim()
    .min(1, "Note is required")
    .max(2000, "Note must be at most 2000 characters"),
  name: z.string().trim().max(200).nullish(),
});

export const adminCreditGrantSchema = z.object({
  client_id: uuidSchema,
  amount_cents: z
    .number()
    .int("Amount must be an integer (cents)")
    .positive("Amount must be greater than 0")
    .max(10_000_000, "Amount cannot exceed 100,000.00"),
  currency: currencySchema,
  name: z.string().trim().min(1, "Name is required").max(200),
  note: z
    .string()
    .trim()
    .min(1, "Note is required")
    .max(2000, "Note must be at most 2000 characters"),
  category: z.enum(["paid", "promotional"]),
  grant_type: z.literal("invoice_balance_credit"),
});

export const applyDiscountSchema = z.object({
  coupon_ids: z
    .array(z.string().trim().min(1, "Coupon ID is required"))
    .min(1, "Select at least one coupon")
    .max(20, "At most 20 coupons"),
});

export const creditGrantListParamsSchema = z.object({
  status: z.enum(["active", "depleted", "expired", "voided"]).optional(),
  grant_type: z
    .enum(["included_analyses", "bonus_analyses", "invoice_balance_credit"])
    .optional(),
  page: z.number().int().min(1).optional(),
  items_per_page: z.number().int().min(1).max(100).optional(),
});

// =============================================================================
// Inferred types
// =============================================================================

export type CreateStripeProductInput = z.infer<
  typeof createStripeProductSchema
>;
export type CreateStripePriceInput = z.infer<typeof createStripePriceSchema>;
export type CreateStripeMeterInput = z.infer<typeof createStripeMeterSchema>;
export type CreateShippingRateInput = z.infer<typeof createShippingRateSchema>;
export type CreateCouponInput = z.infer<typeof createCouponSchema>;
export type CreatePlanInput = z.infer<typeof createPlanSchema>;
export type UpdatePlanInput = z.infer<typeof updatePlanSchema>;
export type ApplyPricingInput = z.infer<typeof applyPricingSchema>;
export type CreateAddonInput = z.infer<typeof createAddonSchema>;
export type UpdateAddonInput = z.infer<typeof updateAddonSchema>;
export type ClientBillingConfigInput = z.infer<
  typeof clientBillingConfigSchema
>;
export type OneOffInvoiceInput = z.infer<typeof oneOffInvoiceSchema>;
export type AdminMeterGrantInput = z.infer<typeof adminMeterGrantSchema>;
export type AdminCreditGrantInput = z.infer<typeof adminCreditGrantSchema>;
export type ApplyDiscountInput = z.infer<typeof applyDiscountSchema>;
export type CreditGrantListParamsInput = z.infer<
  typeof creditGrantListParamsSchema
>;
